import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, shareReplay, catchError, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from "../../environments/environment";

export interface PermanentDiscountItem {
  id: number;
  titulo: string;
  descripcion: string;
  img: string;
  url: string;
  orden: number;
}

@Injectable({
  providedIn: 'root'
})
export class PermanentDiscountsService {
  private readonly apiUrl = environment.apiUrl+'/descuentos';
  private cachedResponse: PermanentDiscountItem[] | null = null;
  private lastFetchTime = 0;
  private readonly CACHE_DURATION = 10 * 60 * 1000; // 10 minutos
  private isLoading = false;
  private currentRequest$: Observable<PermanentDiscountItem[]> | null = null;
  private isInitialized = false; // Protección contra múltiples inicializaciones

  constructor(private readonly http: HttpClient) {}

  getPermanentDiscounts(): Observable<PermanentDiscountItem[]> {
    const now = Date.now();

    // Si ya hay una petición en curso, retornar la misma
    if (this.isLoading && this.currentRequest$) {
      return this.currentRequest$;
    }

    // Retornar cache si está disponible y no ha expirado
    if (this.cachedResponse && (now - this.lastFetchTime) < this.CACHE_DURATION) {
      return of(this.cachedResponse);
    }

    // Si hay cache expirado, retornarlo mientras se hace nueva petición
    if (this.cachedResponse) {
      return of(this.cachedResponse);
    }

    // Hacer nueva llamada
    console.log('🔄 Descuentos Permanentes: Iniciando nueva petición HTTP');
    this.isLoading = true;

    this.currentRequest$ = this.http.get<PermanentDiscountItem[]>(this.apiUrl).pipe(
      tap(response => {
        console.log('✅ Descuentos Permanentes: Petición HTTP completada');
        this.cachedResponse = response;
        this.lastFetchTime = Date.now();
        this.isLoading = false;
        this.currentRequest$ = null;
      }),
      catchError(error => {
        console.error('❌ Descuentos Permanentes: Error en petición HTTP:', error);
        this.isLoading = false;
        this.currentRequest$ = null;

        // Retornar cache anterior si existe, aunque haya expirado
        if (this.cachedResponse) {
          return of(this.cachedResponse);
        }
        throw error;
      }),
      shareReplay(1) // Compartir el resultado con múltiples suscriptores
    );

    return this.currentRequest$;
  }

  // Método para obtener descuentos ordenados por orden
  getPermanentDiscountsOrdered(): Observable<PermanentDiscountItem[]> {
    return this.getPermanentDiscounts().pipe(
      map(discounts => discounts.sort((a, b) => a.orden - b.orden))
    );
  }

  // Método para obtener descuentos con límite
  getPermanentDiscountsLimited(limit: number): Observable<PermanentDiscountItem[]> {
    return this.getPermanentDiscountsOrdered().pipe(
      map(discounts => discounts.slice(0, limit))
    );
  }

  // Método para limpiar cache (útil para testing o actualizaciones forzadas)
  clearCache(): void {
    console.log('🧹 Descuentos Permanentes: Limpiando cache');
    this.cachedResponse = null;
    this.lastFetchTime = 0;
    this.isLoading = false;
    this.currentRequest$ = null;
  }

  // Método para forzar actualización (ignora cache)
  forceRefresh(): Observable<PermanentDiscountItem[]> {
    console.log('🔄 Descuentos Permanentes: Forzando actualización');
    this.clearCache();
    return this.getPermanentDiscounts();
  }

  // Método para verificar estado del cache
  getCacheStatus(): { hasCache: boolean; isExpired: boolean; isLoading: boolean } {
    const now = Date.now();
    const hasCache = !!this.cachedResponse;
    const isExpired = hasCache && (now - this.lastFetchTime) >= this.CACHE_DURATION;

    return {
      hasCache,
      isExpired,
      isLoading: this.isLoading
    };
  }

  // Método para obtener estadísticas de descuentos
  getDiscountsStatistics(): Observable<{ total: number; averageOrder: number }> {
    return this.getPermanentDiscounts().pipe(
      map(discounts => {
        const total = discounts.length;
        const averageOrder = total > 0
          ? discounts.reduce((sum, discount) => sum + discount.orden, 0) / total
          : 0;

        return { total, averageOrder };
      })
    );
  }
}
